import { AxiosInstance, AxiosResponse } from "axios";
import {
  colorNameToToken,
  tokenToColorName,
} from "settings/account/components/TagsManager/colorUtils";
import {
  AddMemberInput,
  AddSubscriptionInput,
  GroupAssignment,
  GroupMember,
  GroupTagResource,
  NotificationGroup,
  Recipient,
  ServiceSubscription,
} from "../types";

const BASE = "/notifications-v2";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  rejected?: unknown;
}

/**
 * API wire shape for group metadata (color is a semantic name, e.g. `"red"`).
 * `linkedTagId` travels as a number on the wire (the contract is `number | null`);
 * the UI keeps it as a string (Tag.id is a string), so it's coerced in
 * `normalizeGroupBodyForApi` before each create/update.
 */
type GroupMetaBody = {
  name?: string;
  color?: string | null;
  description?: string | null;
  linkedTagId?: string | number | null;
};

/** CSS var token → semantic name for create/update payloads (Tags Manager parity). */
function colorForApi(
  color: string | null | undefined
): string | null | undefined {
  if (color === undefined) return undefined;
  if (color === null) return null;
  if (!color.startsWith("var(")) return color;
  return tokenToColorName(color);
}

/** Semantic name → CSS var token for UI state (picker, table swatch, badges). */
function colorFromApi(color: string | null | undefined): string | null {
  if (!color) return null;
  if (color.startsWith("var(")) return color;
  return colorNameToToken(color);
}

/** API may return camelCase or snake_case; coerce tag id to string for UI. */
function linkedTagIdFromApi(
  group: NotificationGroup & { linked_tag_id?: string | number | null }
): string | null {
  const raw = group.linkedTagId ?? group.linked_tag_id;
  if (raw == null || raw === "") return null;
  return String(raw);
}

function normalizeTagResource(
  row: GroupTagResource & {
    tag_id?: number | string;
    resource_subtitle?: string | null;
  }
): GroupTagResource {
  return {
    ...row,
    tagId: String(row.tagId ?? row.tag_id ?? ""),
    resourceId: String(row.resourceId),
    subtitle: row.subtitle ?? row.resource_subtitle ?? null,
  };
}

function tagResourcesFromApi(
  group: NotificationGroup & {
    tag_resources?: GroupTagResource[];
  }
): GroupTagResource[] | undefined {
  const raw = group.tagResources ?? group.tag_resources;
  if (!raw?.length) return undefined;
  return raw.map(normalizeTagResource);
}

function normalizeGroup(
  group: NotificationGroup & {
    linked_tag_id?: string | number | null;
    tag_resources?: GroupTagResource[];
  }
): NotificationGroup {
  return {
    ...group,
    color: colorFromApi(group.color),
    linkedTagId: linkedTagIdFromApi(group),
    tagResources: tagResourcesFromApi(group),
  };
}

/**
 * UI tag id (string) → wire `number | null`. The contract is `number | null`;
 * sending `"42"` instead of `42` can fail strict backend validation / lookups.
 * `undefined` is preserved so a PATCH that doesn't touch the field stays clean.
 */
function linkedTagIdForApi(
  linkedTagId: string | number | null | undefined
): number | null | undefined {
  if (linkedTagId === undefined) return undefined;
  if (linkedTagId === null || linkedTagId === "") return null;
  const n = Number(linkedTagId);
  return Number.isFinite(n) ? n : null;
}

function normalizeGroupBodyForApi<T extends GroupMetaBody>(body: T): T {
  const out: T = { ...body };
  if ("color" in body) out.color = colorForApi(body.color);
  if ("linkedTagId" in body) {
    out.linkedTagId = linkedTagIdForApi(body.linkedTagId);
  }
  return out;
}

function normalizeGroupsEnvelope<T extends ApiEnvelope<NotificationGroup[]>>(
  res: AxiosResponse<T>
): AxiosResponse<T> {
  return {
    ...res,
    data: {
      ...res.data,
      data: res.data.data.map(normalizeGroup),
    },
  };
}

function normalizeGroupEnvelope<T extends ApiEnvelope<NotificationGroup>>(
  res: AxiosResponse<T>
): AxiosResponse<T> {
  return {
    ...res,
    data: { ...res.data, data: normalizeGroup(res.data.data) },
  };
}

// ---------- Groups ----------

export async function listGroups(
  api: AxiosInstance,
  tenantId: number,
  include: string[] = ["counts"]
): Promise<AxiosResponse<ApiEnvelope<NotificationGroup[]>>> {
  const qs = new URLSearchParams({ tenantId: String(tenantId) });
  if (include.length) qs.set("include", include.join(","));
  const res = await api.get<ApiEnvelope<NotificationGroup[]>>(
    `${BASE}/groups?${qs.toString()}`
  );
  return normalizeGroupsEnvelope(res);
}

export async function createGroup(
  api: AxiosInstance,
  tenantId: number,
  body: {
    name: string;
    color?: string | null;
    description?: string | null;
    linkedTagId?: string | null;
  }
): Promise<AxiosResponse<ApiEnvelope<NotificationGroup>>> {
  const res = await api.post<ApiEnvelope<NotificationGroup>>(
    `${BASE}/groups?tenantId=${tenantId}`,
    normalizeGroupBodyForApi(body)
  );
  return normalizeGroupEnvelope(res);
}

export async function getGroup(
  api: AxiosInstance,
  tenantId: number,
  groupId: string
): Promise<AxiosResponse<ApiEnvelope<NotificationGroup>>> {
  const res = await api.get<ApiEnvelope<NotificationGroup>>(
    `${BASE}/groups/${groupId}?tenantId=${tenantId}`
  );
  return normalizeGroupEnvelope(res);
}

export async function updateGroup(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  body: {
    name?: string;
    color?: string | null;
    description?: string | null;
    linkedTagId?: string | null;
  }
): Promise<AxiosResponse<ApiEnvelope<NotificationGroup>>> {
  const res = await api.put<ApiEnvelope<NotificationGroup>>(
    `${BASE}/groups/${groupId}?tenantId=${tenantId}`,
    normalizeGroupBodyForApi(body)
  );
  return normalizeGroupEnvelope(res);
}

export function deleteGroup(
  api: AxiosInstance,
  tenantId: number,
  groupId: string
): Promise<AxiosResponse<ApiEnvelope<null>>> {
  return api.delete(`${BASE}/groups/${groupId}?tenantId=${tenantId}`);
}

// ---------- Members ----------

export function listMembers(
  api: AxiosInstance,
  tenantId: number,
  groupId: string
): Promise<AxiosResponse<ApiEnvelope<GroupMember[]>>> {
  return api.get(`${BASE}/groups/${groupId}/members?tenantId=${tenantId}`);
}

/** Fields the notifications API accepts (draft-only keys like `pickerTenantId` are dropped). */
function membersForApi(
  members: AddMemberInput[]
): Array<
  | { type: "user"; auth0Id: string; email: string; displayName?: string }
  | { type: "email"; email: string; displayName?: string }
> {
  return members.map((m) =>
    m.type === "user"
      ? {
          type: "user" as const,
          auth0Id: m.auth0Id,
          email: m.email,
          ...(m.displayName ? { displayName: m.displayName } : {}),
        }
      : {
          type: "email" as const,
          email: m.email,
          ...(m.displayName ? { displayName: m.displayName } : {}),
        }
  );
}

export function addMembers(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  members: AddMemberInput[]
): Promise<AxiosResponse<ApiEnvelope<GroupMember[]>>> {
  return api.post(`${BASE}/groups/${groupId}/members?tenantId=${tenantId}`, {
    members: membersForApi(members),
  });
}

export function removeMember(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  memberId: string
): Promise<AxiosResponse<ApiEnvelope<null>>> {
  return api.delete(
    `${BASE}/groups/${groupId}/members/${memberId}?tenantId=${tenantId}`
  );
}

// ---------- Assignments ----------

export function listAssignments(
  api: AxiosInstance,
  tenantId: number,
  groupId: string
): Promise<AxiosResponse<ApiEnvelope<GroupAssignment[]>>> {
  return api.get(`${BASE}/groups/${groupId}/assignments?tenantId=${tenantId}`);
}

export function addAssignments(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  assignments: {
    resourceType: string;
    resourceId: string;
    eventTypes?: string[] | null;
  }[]
): Promise<AxiosResponse<ApiEnvelope<GroupAssignment[]>>> {
  return api.post(
    `${BASE}/groups/${groupId}/assignments?tenantId=${tenantId}`,
    { assignments }
  );
}

export function removeAssignment(
  api: AxiosInstance,
  tenantId: number,
  groupId: string,
  assignmentId: string
): Promise<AxiosResponse<ApiEnvelope<null>>> {
  return api.delete(
    `${BASE}/groups/${groupId}/assignments/${assignmentId}?tenantId=${tenantId}`
  );
}

// ---------- Resource-centric ----------

export function listResourceGroups(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number
): Promise<AxiosResponse<ApiEnvelope<NotificationGroup[]>>> {
  return api.get(
    `${BASE}/resources/${resourceType}/${resourceId}/groups?tenantId=${tenantId}`
  );
}

export function setResourceGroups(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number,
  groups: string[] | { groupId: string; eventTypes?: string[] | null }[]
): Promise<AxiosResponse<ApiEnvelope<GroupAssignment[]>>> {
  const body =
    groups.length === 0 || typeof groups[0] === "string"
      ? { groupIds: groups as string[] }
      : {
          groups: groups as {
            groupId: string;
            eventTypes?: string[] | null;
          }[],
        };
  return api.put(
    `${BASE}/resources/${resourceType}/${resourceId}/groups?tenantId=${tenantId}`,
    body
  );
}

export function listRecipients(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number,
  eventType?: string
): Promise<
  AxiosResponse<
    ApiEnvelope<{
      resourceType: string;
      resourceId: string;
      eventType: string | null;
      recipients: Recipient[];
    }>
  >
> {
  const qs = new URLSearchParams({ tenantId: String(tenantId) });
  if (eventType) qs.set("eventType", eventType);
  return api.get(
    `${BASE}/resources/${resourceType}/${resourceId}/recipients?${qs.toString()}`
  );
}

export function listResourceTypes(
  api: AxiosInstance,
  tenantId: number
): Promise<AxiosResponse<ApiEnvelope<string[]>>> {
  return api.get(`${BASE}/resource-types?tenantId=${tenantId}`);
}

// ---------- Personal preferences ("My Notifications") ----------

export interface UserPreference {
  resourceType: string;
  enabled: boolean;
  eventTypes: string[] | null;
  updatedAt: string | null;
}

export function getMyPreferences(
  api: AxiosInstance,
  tenantId: number
): Promise<AxiosResponse<ApiEnvelope<UserPreference[]>>> {
  return api.get(`${BASE}/me/preferences?tenantId=${tenantId}`);
}

export function putMyPreferences(
  api: AxiosInstance,
  tenantId: number,
  preferences: Array<{
    resourceType: string;
    enabled: boolean;
    eventTypes?: string[] | null;
  }>
): Promise<AxiosResponse<ApiEnvelope<UserPreference[]>>> {
  return api.put(`${BASE}/me/preferences?tenantId=${tenantId}`, {
    preferences,
  });
}

// ---------- Self-service service-based subscriptions ----------

export function listSubscriptions(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number
): Promise<AxiosResponse<ApiEnvelope<ServiceSubscription[]>>> {
  return api.get(
    `${BASE}/resources/${resourceType}/${resourceId}/subscriptions?tenantId=${tenantId}`
  );
}

export function addSubscriptions(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number,
  subscriptions: AddSubscriptionInput[]
): Promise<AxiosResponse<ApiEnvelope<ServiceSubscription[]>>> {
  return api.post(
    `${BASE}/resources/${resourceType}/${resourceId}/subscriptions?tenantId=${tenantId}`,
    { subscriptions }
  );
}

export function updateSubscription(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number,
  subscriptionId: string,
  body: { eventTypes?: string[] | null; displayName?: string }
): Promise<AxiosResponse<ApiEnvelope<ServiceSubscription>>> {
  return api.put(
    `${BASE}/resources/${resourceType}/${resourceId}/subscriptions/${subscriptionId}?tenantId=${tenantId}`,
    body
  );
}

export function removeSubscription(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string,
  resourceId: string | number,
  subscriptionId: string
): Promise<AxiosResponse<ApiEnvelope<null>>> {
  return api.delete(
    `${BASE}/resources/${resourceType}/${resourceId}/subscriptions/${subscriptionId}?tenantId=${tenantId}`
  );
}
